import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { MessageDialogComponent, MessageBoxCustomButton } from 'src/app/angular-core/components/message-dialog/message-dialog.component';
import { DialogAnimationService } from 'src/app/angular-core/services/dialog-animation.service';
import { SaveSheetService } from 'src/app/angular-core/services/save-sheet.service';
import { ManualSubscription, RemoteMaintenanceFlags } from 'src/app/generated/CrossPoint.License.Server';
import { BasicXpacInfo } from 'src/app/generated/CrossPoint.RemoteXpac.Server';
import { setSubscriptionAction, LicensesActionsTypes, getSubscriptionAction } from 'src/app/store/actions/licenses.actions';
import { selectUserData } from 'src/app/store/selectors/auth.selectors';
import { selectLicensesPermissions, selectManualSubscription } from 'src/app/store/selectors/licenses.selectors';
import { OperationEnum } from 'src/app/utils/enums/operation.enums';
import { Roles } from 'src/app/utils/enums/roles';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { dateValidatorPast } from 'src/app/utils/functions/date.functions';
import { createReference, updateSaveSheet } from 'src/app/utils/functions/form.functions';
import { hasApiPermission, hasRoleUserData } from 'src/app/utils/functions/permission.function';
import { IUserData } from 'src/app/utils/interfaces/auth.interfaces';
import { IManualSubscriptionState, IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';

@UntilDestroy()
@Component({
  selector: 'xpac-xpac-license',
  templateUrl: './xpac-license.component.html',
  styleUrls: ['./xpac-license.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class XpacLicenseComponent implements OnInit {

  public isLoading: boolean = true;
  public formGroupXPACInfo: UntypedFormGroup;
  public manualSubscription: ManualSubscription;

  @Input() public xpac: BasicXpacInfo = null;

  private referenceFormGroupXPACInfo: object;
  private userData: IUserData = null;

  constructor(private store: Store<IRemoteXPACPortalState>,
    private saveSheetService: SaveSheetService,
    private translate: TranslateService,
    public dialog: DialogAnimationService,
    private actionsListener$: Actions,
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef) { }

  public ngOnInit(): void {
    this.initForm();
    this.initListeners();
    this.cdr.markForCheck();
  }

  public hasCrossPointRole(): boolean {
    return (hasRoleUserData(Roles.CrosspointAdmin, this.userData) || hasRoleUserData(Roles.Crosspoint, this.userData));
  }

  public cancelSubscription(): void {
    let headerText: string = this.translate.instant('Modules.XPACManagementComponent.XpacLicenseComponent.CancelLicenseNowTitle');
    let messageText: string = this.translate.instant('Modules.XPACManagementComponent.XpacLicenseComponent.CancelLicenseNowMessage')

    const dialogRef: MatDialogRef<MessageDialogComponent> = this.dialog.open(MessageDialogComponent, {
      panelClass: 'messagebox-dialog',
      autoFocus: false,
      data: { headerInfo: headerText, message: messageText, buttons: MessageBoxCustomButton.YesNo }
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        const endDate = new Date().toISOString();
        this.store.dispatch(setSubscriptionAction({
          data: {
            serial: this.xpac.deviceId,
            endDate: endDate,
            flags: this.getRemoteMaintenanceFlagsFromForm(),
          }
        }));
      }
    });
  }

  public save(): void {
    const endDate = new Date(this.formGroupXPACInfo.value['endDate']).toISOString();
    this.store.dispatch(setSubscriptionAction({
      data: {
        serial: this.xpac.deviceId,
        endDate: new Date(endDate) <= new Date() ? null : endDate,
        flags: this.getRemoteMaintenanceFlagsFromForm(),
      }
    }));
  }

  private getRemoteMaintenanceFlagsFromForm(): RemoteMaintenanceFlags {
    return ({
      allowCrossPoint: !!this.formGroupXPACInfo.value['allowCrossPoint'],
      allowPartner: !!this.formGroupXPACInfo.value['allowPartner'],
      allowRetailer: !!this.formGroupXPACInfo.value['allowRetailer'],
    });
  }

  private initForm(): void {
    this.formGroupXPACInfo = this.formBuilder.group({
      hasSubscription: [{ value: null, disabled: true }],
      endDate: [{ value: null, disabled: false }, [Validators.required, dateValidatorPast()]],
      allowCrossPoint: [{ value: false, disabled: false }],
      allowPartner: [{ value: false, disabled: false }],
      allowRetailer: [{ value: false, disabled: false }],
    });

    this.referenceFormGroupXPACInfo = createReference(this.formGroupXPACInfo.value);

    this.formGroupXPACInfo.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        updateSaveSheet(this.formGroupXPACInfo, this.referenceFormGroupXPACInfo, this.saveSheetService);
      });
    this.cdr.markForCheck();
  }

  private updateFormPermissions(canEdit: boolean): void {
    if (canEdit) {
      this.formGroupXPACInfo.get('endDate').enable();
    }
    else {
      this.formGroupXPACInfo.get('endDate').disable();
    }
  }

  private setForm(): void {
    this.formGroupXPACInfo.get('hasSubscription').setValue(this.xpac.hasSubscription, { emitEvent: false });
    this.formGroupXPACInfo.get('endDate').setValue(formatDate(this.manualSubscription.endDate, 'yyyy-MM-dd', 'en'), { emitEvent: false });
    this.formGroupXPACInfo.get('allowCrossPoint').setValue(this.manualSubscription.flags?.allowCrossPoint ? this.manualSubscription.flags?.allowCrossPoint : false, { emitEvent: false });
    this.formGroupXPACInfo.get('allowPartner').setValue(this.manualSubscription.flags?.allowPartner ? this.manualSubscription.flags?.allowPartner : false, { emitEvent: false });
    this.formGroupXPACInfo.get('allowRetailer').setValue(this.manualSubscription.flags?.allowRetailer ? this.manualSubscription.flags?.allowRetailer : false, { emitEvent: false });

    this.formGroupXPACInfo.get('endDate').setErrors(null);

    this.referenceFormGroupXPACInfo = createReference(this.formGroupXPACInfo.value);
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  private getDefaultExpirationString(): string {
    let date: Date = new Date();
    const day: string = date.getDate().toString().padStart(2, '0');
    const month: string = (date.getMonth() + 1).toString().padStart(2, '0');
    const year: string = date.getFullYear().toString().padStart(4, '0');

    return `${year}-${month}-${day}`;
  }

  private initListeners(): void {

    this.store.select(selectUserData)
      .pipe(
        filter((value: IUserData | null) => value !== null),
        untilDestroyed(this)
      )
      .subscribe((userData: IUserData) => {
        this.userData = userData;
      });

    this.store.dispatch(getSubscriptionAction({ data: this.xpac.deviceId }));

    this.store.select(selectLicensesPermissions)
      .pipe(
        filter(hasValue),
        untilDestroyed(this),
      )
      .subscribe((permissions) => {
        const canEdit: boolean = hasApiPermission('RemoteMaintenance_SubscriptionEndDate', permissions, OperationEnum.UPDATE);
        this.updateFormPermissions(canEdit);
      });

    this.actionsListener$
      .pipe(ofType(LicensesActionsTypes.GET_SUBSCRIPTION_FAILED))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const flags: RemoteMaintenanceFlags = { allowCrossPoint: false, allowPartner: false, allowRetailer: false }
        this.manualSubscription = { endDate: this.getDefaultExpirationString(), flags: flags };
        this.setForm();
      });

    this.actionsListener$
      .pipe(ofType(LicensesActionsTypes.SET_SUBSCRIPTION_SUCCESS))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.store.dispatch(getSubscriptionAction({ data: this.xpac.deviceId }));
      });

    this.store.select(selectManualSubscription(this.xpac.deviceId))
      .pipe(
        filter(hasValue),
        untilDestroyed(this)
      )
      .subscribe((manualSubscription: IManualSubscriptionState) => {
        this.manualSubscription = manualSubscription.subscription;
        this.setForm();
      });

    // On save event
    this.saveSheetService.savedStatus$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe((isSaved: boolean) => {
        if (isSaved) {
          this.save();
        }
      });
  }
}